import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { useCategories } from "@/api/categories";
import {
  monthLabel,
  monthOf,
  shiftMonth,
  useBudgetActuals,
  useBudgetSuggestions,
  useCreateBudget,
  useDeleteBudget,
} from "@/api/budgets";
import { useHousehold } from "@/app/useHousehold";
import { BudgetBar } from "@/components/BudgetBar";
import { Amount } from "@/components/Amount";
import { AmountError } from "@/money";
import {
  EmptyState,
  ErrorNote,
  Field,
  Loading,
  PageTitle,
  Panel,
  Section,
  buttonClass,
  inputClass,
  quietButtonClass,
  rowsClass,
  secondaryButtonClass,
} from "@/components/ui";
import { violationsOf } from "@/i18n/apiMessages";
import { useFormat } from "@/i18n/format";
import { useT } from "@/i18n/LocaleProvider";

/**
 * The budget screen: bars for the month, and suggestions for what is not
 * budgeted yet.
 *
 * Two rules from `project.md` §5 shape it.
 *
 * **Progress made visible** (§5.3). A bar per budget, showing what is spent
 * against what was planned — the element the reference apps' reviews single out,
 * and cheap to build on top of K-20's figures.
 *
 * **Suggest, don't demand** (§5.4). The add form is offered with amounts already
 * worked out from three to six months of real spending, so a household is
 * choosing rather than guessing at an empty field. The suggestion says where it
 * came from, because a number with no working shown is an oracle.
 *
 * Uncategorised spending is shown beside the budgets. Without it a household
 * could read "everything within budget" over a month where half the money went
 * somewhere nobody classified.
 */
export function BudgetsPage() {
  const t = useT();
  const format = useFormat();
  const { householdId = "" } = useParams();
  const household = useHousehold();
  const currency = household?.base_currency_code ?? "EUR";

  const [period, setPeriod] = useState(() => monthOf(new Date()));

  const actuals = useBudgetActuals(householdId, period);
  const suggestions = useBudgetSuggestions(householdId, period);
  const categories = useCategories(householdId);
  const create = useCreateBudget(householdId);
  const remove = useDeleteBudget(householdId);

  const [categoryId, setCategoryId] = useState("");
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState<string>();

  const nameOf = (id: string): string => {
    for (const parent of categories.data?.items ?? []) {
      if (parent.id === id) return parent.name;
      for (const child of parent.children ?? []) {
        if (child.id === id) return `${parent.name} → ${child.name}`;
      }
    }
    return t("transaction.category");
  };

  const violation = violationsOf(t, create.error);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setAmountError(undefined);

    if (!categoryId) {
      setAmountError(t("budgets.chooseACategory"));
      return;
    }
    let minor: number;
    try {
      minor = Math.abs(format.parse(amount, currency));
    } catch (err) {
      setAmountError(err instanceof AmountError ? t(err.key, err.params) : String(err));
      return;
    }
    if (minor === 0) {
      setAmountError(t("budgets.notZero"));
      return;
    }

    create.mutate(
      { category_id: categoryId, period, amount_minor: minor },
      { onSuccess: () => { setCategoryId(""); setAmount(""); } },
    );
  }

  const rows = actuals.data?.budgets ?? [];
  const proposals = suggestions.data?.items ?? [];

  return (
    <div>
      <PageTitle>{t("nav.budgets")}</PageTitle>

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPeriod(shiftMonth(period, -1))}
          aria-label={t("budgets.previousMonth")}
          className={`${secondaryButtonClass} min-h-11 px-3`}
        >
          ←
        </button>
        <span
          className="min-w-40 text-center text-sm font-medium text-ink"
          data-testid="budget-period"
        >
          {monthLabel(period, format.locale)}
        </span>
        <button
          type="button"
          onClick={() => setPeriod(shiftMonth(period, 1))}
          aria-label={t("budgets.nextMonth")}
          className={`${secondaryButtonClass} min-h-11 px-3`}
        >
          →
        </button>
      </div>

      {actuals.isLoading && <Loading />}
      <ErrorNote error={actuals.error} />
      <ErrorNote error={remove.error} />

      {!actuals.isLoading && rows.length === 0 && (
        <EmptyState title={t("budgets.emptyTitle")}>{t("budgets.emptyBody")}</EmptyState>
      )}

      {rows.length > 0 && (
        <ul className={`mt-6 ${rowsClass}`}>
          {rows.map((row) => (
            <li key={row.budget.id} data-testid="budget-row" className="py-4">
              <BudgetBar
                label={nameOf(row.budget.category_id)}
                spentMinor={row.spent_minor}
                budgetMinor={row.budget.amount_minor}
                currency={currency}
                ratio={row.used_ratio}
                isOverspent={row.is_overspent}
              />
              <button
                type="button"
                onClick={() => remove.mutate(row.budget.id)}
                disabled={remove.isPending}
                className={`${quietButtonClass} mt-1 min-h-11`}
              >
                {t("budgets.remove")}
              </button>
            </li>
          ))}
        </ul>
      )}

      {(actuals.data?.uncategorised_lines ?? 0) > 0 && (
        <p className="mt-6 text-sm text-muted" data-testid="uncategorised">
          {t("budgets.uncategorisedThisMonth")}{" "}
          <Amount
            minor={actuals.data?.uncategorised_minor ?? 0}
            currency={currency}
            testId="uncategorised-amount"
          />{" "}
          {t("budgets.noBudgetCovers")}
        </p>
      )}

      <Panel className="mt-8 max-w-md">
        <form onSubmit={onSubmit} noValidate>
          <p className="text-sm font-medium text-ink">{t("budgets.add")}</p>

          <Field label="Kategorie" htmlFor="budget-category" error={violation("category_id")}>
            <select
              id="budget-category"
              name="category_id"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className={`${inputClass} min-h-11 text-base sm:text-sm`}
            >
              <option value="">{t("budgets.chooseACategory")}</option>
              {(categories.data?.items ?? []).map((parent) => (
                <optgroup key={parent.id} label={parent.name}>
                  <option value={parent.id}>{parent.name} (gesamt)</option>
                  {(parent.children ?? []).map((child) => (
                    <option key={child.id} value={child.id}>
                      {child.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>

          <Field
            label={`${t("transactions.amount")} (${currency})`}
            htmlFor="budget-amount"
            hint={t("budgets.amountHint")}
            error={amountError ?? violation("amount_minor")}
          >
            <input
              id="budget-amount"
              name="amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              placeholder={format.plain(40_000, currency)}
              className={`${inputClass} num min-h-11 text-base sm:text-sm`}
            />
          </Field>

          <button
            type="submit"
            disabled={create.isPending}
            className={`${buttonClass} mt-4 min-h-11`}
          >
            {create.isPending ? t("common.saving") : t("budgets.set")}
          </button>
          <ErrorNote error={create.error} />
        </form>
      </Panel>

      {proposals.length > 0 && (
        <Section title={t("budgets.suggestions")}>
          <p className="text-xs text-muted">{t("budgets.suggestionsHint")}</p>
          <ul className="mt-3 space-y-2">
            {proposals.map((suggestion) => (
              <li key={suggestion.category_id} data-testid="budget-suggestion">
                <button
                  type="button"
                  onClick={() => {
                    setCategoryId(suggestion.category_id);
                    // formatPlain, not a division by 100: the scale comes from
                    // the currency (`model.md` §2.2 rule 2), and it produces
                    // exactly the form the field parses back.
                    setAmount(format.plain(suggestion.amount_minor, currency));
                  }}
                  className={`${secondaryButtonClass} flex min-h-11 w-full items-center justify-between gap-3 text-left`}
                >
                  <span>{nameOf(suggestion.category_id)}</span>{" "}
                  <span className="num">
                    <Amount
                      minor={suggestion.amount_minor}
                      currency={currency}
                      testId="suggestion-amount"
                    />
                  </span>
                </button>
                <p className="mt-0.5 text-xs text-muted">
                  {t("budgets.typicalMonth", { months: suggestion.months })}{" "}
                  <Amount minor={suggestion.typical_minor} currency={currency} testId="typical" />
                </p>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}
